/**
 * Отметка строк списка галочками — одиночная и диапазоном.
 *
 * Здесь нет ни React, ни DOM: состояние отметки — это множество ключей плюс
 * ЯКОРЬ, и обе величины считаются чистыми функциями. Так диапазон
 * «первая строка … десятая строка» проверяется тестом без единого клика, а
 * экран остаётся тем, чем должен быть, — разметкой.
 *
 * Ключ строки — номер МЕСТА (`place_id`), а не позиция в списке: список
 * пересортируется на следующем снимке остатков ВБ, и отметка, привязанная к
 * позиции, переехала бы на чужой короб.
 */

export type Selection<K> = {
  /** Отмеченные ключи. Порядок здесь ничего не значит — его задаёт список. */
  keys: ReadonlySet<K>;
  /**
   * Строка последнего ОДИНОЧНОГО клика. От неё считается диапазон, и она же
   * переживает клик с модификатором: два подряд Shift-клика меряются от
   * одного места, а не от предыдущего.
   */
  anchor: K | null;
};

/** Пустая отметка. Функция, а не константа: `Set` изменяем, общий на всех опасен. */
export function empty<K>(): Selection<K> {
  return { keys: new Set<K>(), anchor: null };
}

/**
 * Клик по галочке строки.
 *
 * `range` — был ли зажат модификатор. Их ТРИ (Shift, Ctrl, ⌘) и делают они
 * здесь одно и то же: пользователь просит «от той строки до этой». Разводить
 * Ctrl и Shift по разным смыслам — привычка файловых менеджеров, а на складе
 * человек жмёт то, что первым попало под палец.
 *
 * Диапазон только ДОБАВЛЯЕТ, а не переопределяет отметку: снять — это клик по
 * самой галочке или кнопка «снять». Переопределение стирало бы уже отмеченное
 * в других местах списка, а его отмечали руками.
 */
export function click<K>(
  state: Selection<K>,
  order: readonly K[],
  key: K,
  range: boolean,
): Selection<K> {
  const span = range ? between(order, state.anchor, key) : null;
  if (span) {
    const keys = new Set(state.keys);
    for (const k of span) keys.add(k);
    return { keys, anchor: state.anchor };
  }

  // Одиночный клик: переключить и встать якорем — даже если отметку СНЯЛИ.
  // Следующий Shift-клик меряется от строки, которой человек только что
  // коснулся, а не от той, что была до неё.
  const keys = new Set(state.keys);
  if (keys.has(key)) keys.delete(key);
  else keys.add(key);
  return { keys, anchor: key };
}

/**
 * Отрезок списка между якорем и кликнутой строкой, включительно.
 *
 * `null`, когда диапазона нет: якоря ещё не было, или одна из строк ушла из
 * списка (сменился отбор, приехал новый снимок). Тогда клик с модификатором
 * работает как обычный — молча не отметить ничего было бы хуже.
 */
function between<K>(
  order: readonly K[],
  anchor: K | null,
  key: K,
): K[] | null {
  if (anchor === null) return null;
  const from = order.indexOf(anchor);
  const to = order.indexOf(key);
  if (from < 0 || to < 0) return null;
  return from <= to ? order.slice(from, to + 1) : order.slice(to, from + 1);
}

/** Отметить всё показанное. Якорь сбрасывается: «всё» — не точка отсчёта. */
export function selectAll<K>(order: readonly K[]): Selection<K> {
  return { keys: new Set(order), anchor: null };
}

/** Отмеченные строки В ПОРЯДКЕ СПИСКА и только те, что сейчас на экране. */
export function picked<T, K>(
  rows: readonly T[],
  keyOf: (row: T) => K,
  state: Selection<K>,
): T[] {
  return rows.filter((row) => state.keys.has(keyOf(row)));
}

/** Что уехало в буфер: сам текст, сколько значений в нём и сколько пропущено. */
export type Copied = { text: string; count: number; skipped: number };

/**
 * Список значений для буфера обмена: без пустых и без повторов, склеенный
 * заданным разделителем.
 *
 * Пустые выкидываются, потому что у короба может не быть кода места, и
 * «7261, , 396» в чужом поле поиска — это молчаливая порча списка. Повтор
 * выкидывается по той же причине: вставляют такой список туда, где он
 * становится отбором, а дубль в отборе ничего не добавляет.
 *
 * Сколько значений реально уехало — видно по `count`, и экран обязан сказать
 * это вслух: буфер обмена невидим, проверить его нечем.
 */
export function joinList(values: readonly string[], sep: string): Copied {
  const out: string[] = [];
  const seen = new Set<string>();
  let skipped = 0;
  for (const raw of values) {
    const value = raw.trim();
    if (!value) {
      skipped += 1;
      continue;
    }
    if (seen.has(value)) continue;
    seen.add(value);
    out.push(value);
  }
  return { text: out.join(sep), count: out.length, skipped };
}

/** Через запятую — так уносит список раздел «Короба» (в поиск, в сообщение). */
export function commaList(values: readonly string[]): Copied {
  return joinList(values, ", ");
}

/**
 * КАЖДОЕ ЗНАЧЕНИЕ С НОВОЙ СТРОКИ — так уносит списки раздел «Баркод»
 * (задача владельца 09.09.26).
 *
 * Разделитель тут не вкусовщина: столбец вставляется в таблицу ячейка за
 * ячейкой, а строка через запятую попадает в одну клетку и требует разбора
 * руками.
 */
export function lineList(values: readonly string[]): Copied {
  return joinList(values, "\n");
}
